import { useNavigation } from "@react-navigation/native";
import {
  LayoutNodeFilterType,
  LayoutNodeInput,
} from "@towersystems/roam-common/lib/generated-types";
import { capitalize } from "lodash";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Colors, Spacings, View } from "react-native-ui-lib";
import {
  Card,
  FormLayout,
  TextField,
  FlatActionList,
  FlatActionListItem,
} from "@towersystems/roam-common-native";
import { LayoutScreens } from "../../../../utilities/layout/constants";
import { useFilterQuickKeyFormContext } from "./hooks";
import { FilterEntity } from "./types";
import { toTitleCase } from "../../../../utilities/to-title-case";
import { Image, TouchableOpacity } from "react-native";
import { ItemConfigType } from "../../../../utilities/layout";
import * as ImagePicker from "expo-image-picker";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export interface LayoutNodeFilterFormProps {}

const filterTypeToListScreenMap = {
  [LayoutNodeFilterType.CATEGORY]: LayoutScreens.CategoryListScreen,
  [LayoutNodeFilterType.DEPARTMENT]: LayoutScreens.DepartmentListScreen,
};

export function LayoutNodeFilterForm({
  item,
}: {
  item?: ItemConfigType | undefined;
}) {
  const { entities, handleEntitySelect } = useFilterQuickKeyFormContext();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const navigation = useNavigation<any>();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<LayoutNodeInput>();

  const filter = useWatch({
    control: control,
    name: "filter",
  });

  const watchedBase64Image = useWatch({
    control: control,
    name: "base64Image",
  });

  const watchedBackgroundColor = useWatch({
    control: control,
    name: "color",
  });

  const watchedLabelColour =
    useWatch({
      control: control,
      name: "labelColor",
    }) || Colors.white;

  const filterType = filter?.type || LayoutNodeFilterType.CATEGORY;
  const filterName = capitalize(filterType);

  const flatActionListItems: FlatActionListItem[] =
    entities?.map((value: FilterEntity) => ({
      key: value.id,
      title: `Remove - ${toTitleCase(value.name)}`,
      subTitle: value.description,
      destructive: true,
      onPress: () => {
        handleEntitySelect(value);
      },
    })) || [];

  const otherMenuItems: FlatActionListItem[] = [
    {
      key: "colour",
      title: "Choose Background Colour",
      subTitle: "Background colour will be used where no product image",
      componentRight: () => (
        <View>
          <TouchableOpacity
            style={{
              padding: Spacings.s4,
              width: 60,
              height: 60,
              borderRadius: 8,
              backgroundColor: watchedBackgroundColor,
              borderWidth: 2,
              borderColor: "#00000025",
            }}
            onPress={() =>
              navigation.navigate("ColorPickerScreen", {
                fieldName: "color",
              })
            }
          ></TouchableOpacity>
        </View>
      ),
    },
    {
      key: "labelColour",
      title: "Choose Label Colour",
      subTitle: "Set the colour of the label text on the quick key",
      componentRight: () => (
        <View>
          <TouchableOpacity
            style={{
              padding: Spacings.s4,
              width: 60,
              height: 60,
              borderRadius: 8,
              backgroundColor: watchedLabelColour,
              borderWidth: 2,
              borderColor: "#00000025",
            }}
            onPress={() =>
              navigation.navigate("ColorPickerScreen", {
                fieldName: "labelColor",
              })
            }
          ></TouchableOpacity>
        </View>
      ),
    },
    {
      key: "label",
      title: "Label",
      subTitle:
        "Set a custom label for this quick key. If empty, the filter default name will be used.",
      componentRight: () => (
        <Controller
          name="label"
          render={({ field }) => (
            <TextField
              placeholder="Add a label..."
              value={field.value}
              onChangeText={field.onChange}
            />
          )}
        />
      ),
    },
  ];

  if (!watchedBase64Image?.base64) {
    otherMenuItems.push({
      key: "pick_image",
      title: "Pick Image from Device",
      subTitle: "Select an image from your device to use for this quick key",
      onPress: async () => {
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: false,
          aspect: [1, 1],
          quality: 0.7,
          base64: true,
        });
        if (!result.canceled && result.assets && result.assets.length > 0) {
          const asset = result.assets[0];
          const imageName =
            asset.fileName || asset.uri?.split("/").pop() || "logo.jpg";
          setValue("base64Image", {
            base64: asset.base64 || "",
            imageName,
          });
        }
      },
    });
    if (strategyEnabled("generate_quick_key_icon")) {
      otherMenuItems.push({
        key: "generate_image",
        title: "Generate Button Icon with AI",

        subTitle:
          "Use AI to generate a background image for this product. Requires an AI image generation model to be configured in Settings. ",
        onPress: () =>
          navigation.navigate("GenerateAiIconRootScreen", {
            screen: "GenerateIconScreen",
            description: "",
          }),
      });
    }
  } else {
    otherMenuItems.push({
      key: "remove_image",
      destructive: true,
      title: "Remove Image",
      subTitle: "Remove the custom image set for this quick key",
      onPress: () => {
        setValue("base64Image", undefined, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      componentRight: () => (
        <Image
          source={{
            uri: `data:image/png;base64,${watchedBase64Image?.base64}`,
          }}
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
    });
  }

  if (
    item?.imageUrl &&
    // if watchedBase64 is null or has a value (not undefined)
    watchedBase64Image === undefined
  ) {
    otherMenuItems.push({
      key: "remove_saved_img",
      title: "Remove Quick Key Image",
      subTitle: "Remove any custom image set for this quick key",
      destructive: true,
      onPress: () => {
        // Remove the saved image URL - auto geneated form is incorrect type for api
        // @ts-ignore
        setValue("base64Image", null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      componentRight: () => (
        <Image
          source={{
            uri: item?.imageUrl,
          }}
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
    });
  }
  return (
    <FormLayout>
      <Card title={filterName} containerStyle={{ marginBottom: Spacings.s8 }}>
        <FlatActionList
          items={[
            ...flatActionListItems,
            {
              key: "add",
              title: `Add ${filterName}`,
              onPress: () =>
                navigation.navigate(filterTypeToListScreenMap[filterType]),
            },
          ]}
        />
      </Card>
      <Card title="Other" containerStyle={{ marginBottom: Spacings.s8 }}>
        <FlatActionList items={[...otherMenuItems]} />
      </Card>
    </FormLayout>
  );
}
